import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { 
    ChartBarIcon, 
    ArrowTrendingUpIcon, 
    CurrencyDollarIcon,
    ShoppingBagIcon,
    EyeIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    CalendarIcon,
    TrophyIcon,
    FireIcon,
    ClockIcon,
    ChartPieIcon,
    Squares2X2Icon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';

// Gelişmiş Kategori Analytics Dashboard Komponenti
function AdvancedCategoryAnalyticsPanel() {
    const { token } = useAuth();
    const [analytics, setAnalytics] = useState({
        summary: {},
        topPerformers: {},
        categoryStats: [],
        trends: {},
        loading: true
    });
    const [timeRange, setTimeRange] = useState('30');
    const [selectedView, setSelectedView] = useState('overview');
    const [comparisonCategories, setComparisonCategories] = useState([]);
    const [comparisonData, setComparisonData] = useState(null);

    useEffect(() => {
        fetchAnalytics();
    }, [timeRange]);

    const fetchAnalytics = async () => {
        if (!token) return;

        try {
            setAnalytics(prev => ({ ...prev, loading: true }));
            
            const response = await api.get(`/api/analytics/categories?timeRange=${timeRange}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const responseData = response.data?.data || response.data || {};
            
            setAnalytics({
                summary: responseData.summary || {},
                topPerformers: responseData.topPerformers || {},
                categoryStats: responseData.categoryStats || [],
                trends: responseData.trends || {},
                loading: false
            });
        } catch (error) {
            console.error('Analytics yüklenirken hata:', error);
            toast.error('Analytics verileri yüklenemedi');
            setAnalytics(prev => ({ ...prev, loading: false }));
        }
    };

    const fetchComparisonData = async () => {
        if (comparisonCategories.length < 2) {
            toast.warning('En az 2 kategori seçin');
            return;
        }

        try {
            const categoryIds = comparisonCategories.join(',');
            const response = await api.get(`/api/analytics/categories/comparison?categoryIds=${categoryIds}&timeRange=${timeRange}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setComparisonData(response.data?.data || null);
        } catch (error) {
            console.error('Karşılaştırma verileri yüklenirken hata:', error);
            toast.error('Karşılaştırma verileri yüklenemedi');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount);
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('tr-TR').format(num);
    };

    const getGrowthIcon = (growth) => {
        if (growth > 0) return <ArrowUpIcon className="h-4 w-4 text-green-500" />;
        if (growth < 0) return <ArrowDownIcon className="h-4 w-4 text-red-500" />;
        return <div className="h-4 w-4" />;
    };

    const getGrowthColor = (growth) => {
        if (growth > 0) return 'text-green-600';
        if (growth < 0) return 'text-red-600';
        return 'text-gray-600';
    };

    // Özet Kartları Komponenti
    const SummaryCards = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <Squares2X2Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Toplam Kategori</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics.summary.totalCategories || 0}</p>
                        <p className="text-xs text-gray-500">{analytics.summary.activeCategories || 0} aktif</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Toplam Gelir</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(analytics.summary.totalRevenue || 0)}</p>
                        <p className="text-xs text-gray-500">Son {timeRange} gün</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <ShoppingBagIcon className="h-8 w-8 text-purple-600" />
                    </div>
                    <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Toplam Sipariş</p>
                        <p className="text-2xl font-bold text-gray-900">{formatNumber(analytics.summary.totalOrders || 0)}</p>
                        <p className="text-xs text-gray-500">Son {timeRange} gün</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <ChartBarIcon className="h-8 w-8 text-orange-600" />
                    </div>
                    <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Ortalama Sipariş</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(analytics.summary.averageOrderValue || 0)}</p>
                        <p className="text-xs text-gray-500">Sipariş başına</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <TrophyIcon className="h-8 w-8 text-yellow-600" />
                    </div>
                    <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">En İyi Kategori</p>
                        <p className="text-lg font-bold text-gray-900 truncate">
                            {analytics.topPerformers.topRevenueCategory?.name || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500">Gelir bazında</p>
                    </div>
                </div>
            </div>
        </div>
    );

    // En İyi Performans Kartları
    const TopPerformersCards = () => (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-green-100">En Çok Gelir</p>
                        <p className="text-2xl font-bold">{analytics.topPerformers.topRevenueCategory?.name || 'N/A'}</p>
                        <p className="text-green-100">{formatCurrency(analytics.topPerformers.topRevenueCategory?.revenue || 0)}</p>
                    </div>
                    <TrophyIcon className="h-12 w-12 text-green-200" />
                </div>
            </div>

            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-blue-100">En Hızlı Büyüyen</p>
                        <p className="text-2xl font-bold">{analytics.topPerformers.fastestGrowingCategory?.name || 'N/A'}</p>
                        <p className="text-blue-100">
                            {analytics.topPerformers.fastestGrowingCategory?.growthRate ? 
                                `%${analytics.topPerformers.fastestGrowingCategory.growthRate.toFixed(1)}` : 'N/A'}
                        </p>
                    </div>
                    <FireIcon className="h-12 w-12 text-blue-200" />
                </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-purple-100">En Çok Satan</p>
                        <p className="text-2xl font-bold">{analytics.topPerformers.topSellingCategory?.name || 'N/A'}</p>
                        <p className="text-purple-100">{formatNumber(analytics.topPerformers.topSellingCategory?.totalSales || 0)} adet</p>
                    </div>
                    <ChartBarIcon className="h-12 w-12 text-purple-200" />
                </div>
            </div>
        </div>
    );

    // Detaylı Kategori Tablosu
    const DetailedCategoryTable = () => (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Detaylı Kategori Performansı</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Kategori
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ürün Sayısı
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Satış Adedi
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Gelir
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Sipariş Sayısı
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ort. Sipariş Değeri
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Büyüme
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Son Satış
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Durum
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {analytics.categoryStats?.length > 0 ? (
                            analytics.categoryStats.map((category, index) => (
                                <tr key={category.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {category.icon && (
                                                <i className={`${category.icon} text-brand-green mr-3`}></i>
                                            )}
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{category.name}</div>
                                                <div className="text-sm text-gray-500">{category.slug}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(category.productCount || 0)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(category.totalSales || 0)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {formatCurrency(category.revenue || 0)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(category.orderCount || 0)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatCurrency(category.averageOrderValue || 0)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className={`flex items-center text-sm ${getGrowthColor(category.growthRate)}`}>
                                            {getGrowthIcon(category.growthRate)}
                                            <span className="ml-1">
                                                {category.growthRate ? `%${category.growthRate.toFixed(1)}` : '0%'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {category.lastSaleDate ? 
                                            new Date(category.lastSaleDate).toLocaleDateString('tr-TR') : 
                                            'Henüz satış yok'
                                        }
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            category.is_active 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {category.is_active ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                                    Henüz kategori analytics verisi bulunmuyor
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // Gelir Grafiği
    const RevenueChart = () => (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Kategorilere Göre Gelir Dağılımı</h3>
            <div className="space-y-4">
                {analytics.trends.revenueByCategory?.slice(0, 8).map((category, index) => {
                    const maxRevenue = Math.max(...(analytics.trends.revenueByCategory?.map(c => c.revenue) || [1]));
                    const percentage = maxRevenue > 0 ? (category.revenue / maxRevenue) * 100 : 0;
                    
                    return (
                        <div key={index} className="flex items-center space-x-4">
                            <div className="w-32 text-sm font-medium text-gray-700 truncate">
                                {category.name}
                            </div>
                            <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                                <div 
                                    className="bg-gradient-to-r from-brand-green to-brand-green/80 h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                                    style={{ width: `${Math.max(percentage, 5)}%` }}
                                >
                                    <span className="text-white text-xs font-medium">
                                        {formatCurrency(category.revenue || 0)}
                                    </span>
                                </div>
                            </div>
                            <div className="w-20 text-sm text-gray-600 text-right flex items-center">
                                {getGrowthIcon(category.growth)}
                                <span className={`ml-1 ${getGrowthColor(category.growth)}`}>
                                    %{category.growth?.toFixed(1) || '0'}
                                </span>
                            </div>
                        </div>
                    );
                })}
                
                {(!analytics.trends.revenueByCategory || analytics.trends.revenueByCategory.length === 0) && (
                    <div className="text-center text-gray-500 py-8">
                        <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
                        <p>Henüz gelir verisi bulunmuyor</p>
                        <p className="text-sm mt-1">Siparişler tamamlandıkça veriler burada görünecek</p>
                    </div>
                )}
            </div>
        </div>
    );

    if (analytics.loading) {
        return (
            <div className="space-y-6">
                <div className="animate-pulse">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="bg-gray-200 h-24 rounded-lg"></div>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
                        ))}
                    </div>
                    <div className="bg-gray-200 h-96 rounded-lg"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Gelişmiş Kategori Analytics</h2>
                    <p className="text-gray-600">Kategori performansını detaylı analiz edin</p>
                </div>
                <div className="flex items-center space-x-4">
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                    >
                        <option value="7">Son 7 gün</option>
                        <option value="30">Son 30 gün</option>
                        <option value="90">Son 90 gün</option>
                        <option value="365">Son 1 yıl</option>
                    </select>
                    <button
                        onClick={fetchAnalytics}
                        className="inline-flex items-center px-4 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green-dark transition-colors"
                    >
                        <ArrowPathIcon className="h-4 w-4 mr-2" />
                        Yenile
                    </button>
                </div>
            </div>

            {/* Özet Kartları */}
            <SummaryCards />

            {/* En İyi Performans Kartları */}
            <TopPerformersCards />

            {/* Ana İçerik */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <DetailedCategoryTable />
                </div>
                <div>
                    <RevenueChart />
                </div>
            </div>
        </div>
    );
}

export default AdvancedCategoryAnalyticsPanel;